'use client';

import { useMemo, useState } from 'react';
import { MonitorSmartphoneIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { PERSONALITY_MAX_CHARS, type ThemeChoice } from '@/settings';
import { deleteAccount, logoutEverywhere } from '@/server-threads';
import {
  fieldLabelClass,
  phoneSafeInputTextClass,
  primaryCtaClass,
  textInputClass,
} from '@/components/ui/form-control';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { cn } from '@/lib/utils';

const ALL_TIMEZONES: string[] = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [Intl.DateTimeFormat().resolvedOptions().timeZone, 'Europe/Stockholm', 'UTC'];
  }
})();

// DES1 ST-08: a time zone reads as its place and today's offset, "Sao Paulo (UTC−3)".
const MINUS = '\u2212';

/** Today's offset from UTC in minutes (DST included), or null for a zone this browser lacks. */
function zoneOffsetMinutes(zone: string, at: Date): number | null {
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;
    const match = name ? /^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/.exec(name) : null;
    if (!match) return null;
    const minutes = Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0);
    return match[1] === '-' ? -minutes : minutes;
  } catch {
    return null;
  }
}

/** "UTC", "UTC+2", "UTC−3", "UTC+5:30": a fixed "UTC" and a true minus in every language. */
function offsetLabel(minutes: number) {
  if (!minutes) return 'UTC';
  const abs = Math.abs(minutes);
  const rest = abs % 60;
  return `UTC${minutes < 0 ? MINUS : '+'}${Math.floor(abs / 60)}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
}

/** The zone this id resolves to ("Asia/Kolkata" and "Asia/Calcutta" are one), or null. */
function resolvedZone(zone: string): string | null {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

/** The IANA id's place ("America/Sao_Paulo" → "Sao Paulo"); none for UTC and Etc/ zones. */
/**
 * DES1 ST-09: places IANA has renamed keep their legacy id in many browsers (ICU still lists
 * Europe/Kiev and Asia/Calcutta), so the label uses the current name. Label only: the value stays
 * the browser's id, and search still finds the old name through it. Only true renames of the same
 * place (IANA's backward file); links to a different place are left alone.
 */
const RENAMED_PLACES: Readonly<Record<string, string>> = {
  'Africa/Asmera': 'Asmara',
  'America/Godthab': 'Nuuk',
  'Asia/Ashkhabad': 'Ashgabat',
  'Asia/Calcutta': 'Kolkata',
  'Asia/Chungking': 'Chongqing',
  'Asia/Dacca': 'Dhaka',
  'Asia/Katmandu': 'Kathmandu',
  'Asia/Macao': 'Macau',
  'Asia/Rangoon': 'Yangon',
  'Asia/Saigon': 'Ho Chi Minh',
  'Asia/Thimbu': 'Thimphu',
  'Asia/Ujung_Pandang': 'Makassar',
  'Asia/Ulan_Bator': 'Ulaanbaatar',
  'Atlantic/Faeroe': 'Faroe',
  'Europe/Kiev': 'Kyiv',
  'Europe/Uzhgorod': 'Uzhhorod',
  'Europe/Zaporozhye': 'Zaporizhzhia',
  'Pacific/Enderbury': 'Kanton',
  'Pacific/Ponape': 'Pohnpei',
  'Pacific/Truk': 'Chuuk',
};

function placeOf(zone: string) {
  if (!zone.includes('/') || zone.startsWith('Etc/')) return null;
  return RENAMED_PLACES[zone] ?? zone.slice(zone.lastIndexOf('/') + 1).replace(/_/g, ' ');
}

/**
 * The time zone choices after Automatic: a saved zone this browser does not know first, as its
 * raw id; then west to east by today's offset, then by place. The value is always the IANA id.
 */
export function timezoneOptions(
  zones: readonly string[],
  saved: string,
  locale: string,
  at: Date = new Date(),
): ComboboxOption[] {
  let collator: Intl.Collator;
  try {
    collator = new Intl.Collator(locale);
  } catch {
    collator = new Intl.Collator();
  }
  const known: { value: string; label: string; offset: number; place: string }[] = [];
  const unknown: ComboboxOption[] = [];
  // One row per place: a saved alias of a listed zone replaces it, keeping the saved id as the
  // value and its own place ("Kolkata", not "Calcutta"); nothing is rewritten.
  const offList = Boolean(saved) && !zones.includes(saved);
  const savedAs = offList ? resolvedZone(saved) : null;
  const listed = savedAs ? zones.filter((zone) => resolvedZone(zone) !== savedAs) : zones;
  for (const zone of offList ? [saved, ...listed] : listed) {
    const offset = zoneOffsetMinutes(zone, at);
    if (offset === null) {
      unknown.push({ value: zone, label: zone });
      continue;
    }
    const place = placeOf(zone);
    const label = place ? `${place} (${offsetLabel(offset)})` : offsetLabel(offset);
    known.push({ value: zone, label, offset, place: place ?? '' });
  }
  known.sort((a, b) => a.offset - b.offset || collator.compare(a.place, b.place));
  return [...unknown, ...known.map(({ value, label }) => ({ value, label }))];
}

const inputClass = cn(textInputClass, 'min-h-9 rounded-sm px-2.5', phoneSafeInputTextClass);
const labelClass = fieldLabelClass;
const helperClass = 'text-muted-foreground text-xs leading-snug';

const THEME_OPTIONS: { value: ThemeChoice; label: 'themeLight' | 'themeDark' | 'themeSystem' }[] = [
  { value: 'light', label: 'themeLight' },
  { value: 'dark', label: 'themeDark' },
  { value: 'system', label: 'themeSystem' },
];

/**
 * Appearance: light, dark or system, saved to the account so it applies on
 * every device. A save that fails is said here (the device has already gone
 * back to what it showed), so a choice never looks saved when it is not.
 */
export function AppearanceField({
  choice,
  onChoose,
}: {
  choice: ThemeChoice;
  onChoose: (choice: ThemeChoice) => void | Promise<void>;
}) {
  const copy = useChatCopy();
  const [failed, setFailed] = useState(false);
  const pick = (value: ThemeChoice) => {
    setFailed(false);
    void Promise.resolve(onChoose(value)).catch(() => setFailed(true));
  };
  return (
    <div className="flex flex-col gap-2">
      <span className={labelClass} id="settings-theme-label">
        {copy.appearance}
      </span>
      <div
        className="company-segmented"
        role="group"
        aria-labelledby="settings-theme-label"
        data-testid="settings-theme-choice"
      >
        {THEME_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className="company-secondary"
            aria-pressed={choice === option.value}
            data-theme-choice={option.value}
            onClick={() => pick(option.value)}
          >
            {copy[option.label]}
          </button>
        ))}
      </div>
      <span className={helperClass}>{copy.themeChoiceHelp}</span>
      {failed && (
        <p role="alert" className="company-error">
          {copy.themeSaveFailed}
        </p>
      )}
    </div>
  );
}

export function PersonalityField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const copy = useChatCopy();
  return (
    <div className="flex flex-col gap-1">
      <label className={labelClass} htmlFor="settings-personality">
        {copy.personality}
      </label>
      <div className={helperClass}>{copy.personalityHelp}</div>
      <textarea
        id="settings-personality"
        value={value}
        maxLength={PERSONALITY_MAX_CHARS}
        disabled={disabled}
        onChange={(event) => onChange((event.target as HTMLTextAreaElement).value)}
        placeholder={copy.personalityPlaceholder}
        className={inputClass + ' min-h-28 resize-y p-2'}
      />
      <div className="text-muted-foreground self-end text-xs tabular-nums">
        {value.length} / {PERSONALITY_MAX_CHARS}
      </div>
    </div>
  );
}

export function TimezoneField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const copy = useChatCopy();
  const locale = useChatInterfaceLocale();
  // Automatic first; a saved zone this browser does not list stays shown and kept.
  const options = useMemo(
    () => [
      { value: '', label: copy.automaticBrowser },
      ...timezoneOptions(ALL_TIMEZONES, value, locale),
    ],
    [copy.automaticBrowser, value, locale],
  );
  return (
    <div className="flex flex-col gap-1">
      <label className={labelClass} htmlFor="settings-timezone">
        {copy.timezone}
      </label>
      <Combobox
        id="settings-timezone"
        label={copy.timezone}
        value={value}
        options={options}
        disabled={disabled}
        onChange={onChange}
        placeholder={copy.searchTimezone}
        className={inputClass}
      />
    </div>
  );
}

/** Log out on every device and browser, this one included, after a confirmation; then `onDone` shows login. */
export function LogoutEverywhereSection({ onDone }: { onDone: () => void }) {
  const copy = useChatCopy();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const logout = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await logoutEverywhere();
      setConfirmOpen(false);
      onDone();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="border-border border-l pl-4">
      <div className="flex items-start gap-3">
        <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-full">
          <MonitorSmartphoneIcon className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{copy.logoutEverywhereTitle}</div>
          <p className={cn(helperClass, 'mt-0.5')}>{copy.logoutEverywhereHelp}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          setFailed(false);
          setConfirmOpen(true);
        }}
        className="border-border hover:bg-muted/80 mt-4 min-h-10 rounded-lg border px-4 text-sm font-medium"
      >
        {copy.logoutEverywhere}
      </button>
      <Dialog open={confirmOpen} onOpenChange={(next) => !busy && setConfirmOpen(next)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{copy.logoutEverywhereQuestion}</DialogTitle>
            <DialogDescription>{copy.logoutEverywhereDescription}</DialogDescription>
          </DialogHeader>
          {failed && (
            <div role="alert" className="text-destructive text-sm">
              {copy.logoutEverywhereFailed}
            </div>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmOpen(false)}
              className="border-border min-h-10 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
            >
              {copy.cancel}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void logout()}
              className={cn(primaryCtaClass, 'min-h-10 rounded-md px-4 disabled:opacity-50')}
            >
              {busy ? copy.loggingOut : copy.logoutEverywhere}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function AccountDeletionSection({
  disabled,
  onDeleted,
}: {
  disabled: boolean;
  onDeleted: () => void;
}) {
  const copy = useChatCopy();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const remove = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAccount();
      setConfirmOpen(false);
      onDeleted();
    } catch {
      setDeleteError(copy.accountDeleteFailed);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border-destructive/30 flex flex-col gap-2 border-t pt-4">
      <div>
        <div className={labelClass}>{copy.account}</div>
        <div className={helperClass}>{copy.deleteAccountHelp}</div>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setDeleteError(null);
          setConfirmOpen(true);
        }}
        className="border-destructive/50 text-destructive hover:bg-destructive/10 min-h-9 self-start rounded-md border px-3 text-sm font-semibold disabled:opacity-50"
      >
        {copy.deleteAccount}
      </button>

      <Dialog open={confirmOpen} onOpenChange={(next) => !deleting && setConfirmOpen(next)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{copy.deleteAccountQuestion}</DialogTitle>
            <DialogDescription>{copy.deleteAccountDescription}</DialogDescription>
          </DialogHeader>
          {deleteError && <div className="text-destructive text-sm">{deleteError}</div>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={deleting}
              onClick={() => setConfirmOpen(false)}
              className="border-border min-h-10 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
            >
              {copy.cancel}
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={() => void remove()}
              className="bg-destructive text-destructive-foreground min-h-10 rounded-md px-4 text-sm font-semibold disabled:opacity-50"
            >
              {deleting ? copy.deleting : copy.deleteAccountConfirm}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
